import { FormField, FormItem, FormControl, FormDescription, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { ImagePlusIcon, XIcon } from 'lucide-react';
import { SUPPORTED_FORMATS } from '@/configs/constants';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/hooks/useStore';

interface ImageInputProps {
    form: any,
    previewImage: string | null,
    setPreviewImage: (image: string | null) => void
}

const iconClear = () => (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path fillRule='evenodd' clipRule='evenodd' d='M15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85787 15.5 0.5 12.1421 0.5 8C0.5 3.85787 3.85787 0.5 8 0.5C12.1421 0.5 15.5 3.85787 15.5 8ZM10.5191 10.519C10.2994 10.7387 9.94325 10.7387 9.72358 10.519L8.00001 8.79545L6.27643 10.519C6.05676 10.7387 5.7006 10.7387 5.48093 10.519C5.26126 10.2994 5.26126 9.94321 5.48093 9.72354L7.20451 7.99996L5.48094 6.27638C5.26127 6.05671 5.26127 5.70056 5.48094 5.48089C5.70061 5.26122 6.05677 5.26122 6.27644 5.48089L8.00001 7.20446L9.72357 5.4809C9.94324 5.26123 10.2994 5.26123 10.5191 5.4809C10.7387 5.70057 10.7387 6.05672 10.5191 6.27639L8.7955 7.99996L10.5191 9.72353C10.7387 9.9432 10.7387 10.2994 10.5191 10.519Z' fill='#B5B4B8' />
    </svg>
);

const ImageInput: React.FC<ImageInputProps> = ({
    form,
    previewImage,
    setPreviewImage
}) => {
    const {
        modalStore: { showAlertModal, hideModal }
    } = useStore();
    const labelImagesRef = useRef<HTMLLabelElement>(null);

    const ogImageUrl = form.watch('OgImageUrl');

    const { t } = useTranslation();

    const validateImageFile = (file: File): boolean => {
        const maxSize = 500 * 1024; // 500 KB
        const isValidFormat = SUPPORTED_FORMATS.includes(file.type);
        if (file.size > maxSize || !isValidFormat) {
            showAlertError();
            return false;
        }
        return true;
    };

    const showAlertError = () => {
        showAlertModal({
            type: 'error',
            content: t('seo_settings.image_input_og_image_error'),
            cancelButton: false,
            saveButton: t('button.close'),
            onSave: () => hideModal()
        });
    };
    const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && !validateImageFile(files[0])) {
            e.target.value = '';
            return;
        }
        if (files && files?.length > 0) {
            form.setValue('OGimage', files[0]);
            setPreviewImage(URL.createObjectURL(files[0]));
            e.target.value = '';
        }
    };

    return (
        <>
            <FormField
                control={form.control}
                name='images'
                render={({ field: { onChange, value, ...rest } }) => (
                    <>
                        <FormItem>
                            <FormControl>
                                <Input
                                    id='shop-images'
                                    type='file'
                                    onChange={(event) => {
                                        handleImageFileChange(event);
                                    }}
                                    className='hidden'
                                    accept={SUPPORTED_FORMATS.join(', ')}
                                    {...rest}
                                />
                            </FormControl>
                            <Label
                                ref={labelImagesRef}
                                htmlFor='shop-images'
                                tabIndex={0}
                                className='cursor-pointer'
                            >
                                {previewImage || ogImageUrl ?
                                    (
                                        <div className='relative'>
                                            <img
                                                src={previewImage || ogImageUrl}
                                                alt='Preview'
                                                className='mx-auto h-[188px] w-[310px] rounded object-cover'
                                            />
                                            <div
                                                className='absolute right-1 top-1 cursor-pointer'
                                                tabIndex={-1}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    setPreviewImage(null);
                                                    form.setValue('OGimage', '');
                                                    form.setValue('OgImageUrl', '');
                                                }}
                                            >
                                                {iconClear()}
                                            </div>
                                        </div>
                                    ) :
                                    (
                                        <div
                                            className='relative border-2 border-dashed border-rose-300 rounded-md bg-gray-50 h-[188px] w-[310px] text-center hover:bg-gray-100 transition flex flex-col justify-center items-center space-y-2 text-gray-700'
                                        >
                                            <ImagePlusIcon className='text-active h-6 w-6 mr-2' />
                                            <span className='text-sm'>{t('button.add_shop_image')}</span>
                                        </div>
                                    )}
                            </Label>
                        </FormItem>
                    </>
                )}
            />
            <div className='text-sm text-gray-600 mt-2'>
                <strong>{t('words_title.image_input_og_image_note')}</strong>
                <br />
                {t('words_title.image_input_og_image_recommended')}
                <br />
                {t('words_title.image_input_og_image_file_type')}
                <br />
                {t('words_title.image_input_og_image_max_size')}
            </div>
        </>
    );
};

export default ImageInput;
